import {
  faBullseyeArrow,
  faCashRegister
} from '@fortawesome/pro-light-svg-icons';
import {
  faArrowCircleDown,
  faArrowCircleUp,
  faEquals
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import MarkdownComponent from 'components/shared/MarkdownComponent';
import { useI18n } from 'hooks/useI18n';
import { useRouter } from 'next/router';
import { useState } from 'react';
import type {
  GoalsItem,
  SalesItem,
  SalesItemAttributes
} from '../../BattleReportComponent.types';
import {
  StyledData,
  StyledDataMonth,
  StyledDataMonthList,
  StyledDataMonthTitle,
  StyledGoal,
  StyledGoalIcon,
  StyledGoalsTitle,
  StyledSectionTitle,
  StyledTab,
  StyledTabs,
  StyledTendency,
  StyledTendencyTitle,
  StyledTendencyValue,
  StyledTendencyVariation,
  StyledTitle,
  StyledWrapper
} from './BattleReportQuarter.styled';
import QuarterChart from './QuarterChart';

type ContentProps = {
  sales: SalesItem[];
  goals: GoalsItem[];
};

const BattleReportQuarter: React.FC<ContentProps> = ({ sales, goals }) => {
  const { t } = useI18n('battle-report');
  const { locale } = useRouter();

  const [activeQuarter, setActiveQuarter] = useState(1);

  function setTab(tabNumber: number): void {
    setActiveQuarter(tabNumber);
  }

  const filteredYear = (year: number): SalesItem[] =>
    sales
      .filter((obj) => {
        if (obj.attributes.year === year) {
          return obj;
        }
      })
      .sort((a, b) => a.attributes.month - b.attributes.month);

  const currentYear: SalesItem[] = filteredYear(2023);

  const quarters: number = Math.floor((currentYear.length / 12) * 4);
  const emptyQuarters = 4 - quarters;

  const filteredQuarter = currentYear.filter(
    (el: SalesItem) =>
      el.attributes.month <= activeQuarter * 3 &&
      el.attributes.month >= activeQuarter * 3 - 2
  );
  const getMonthName = (monthNumber: number): string => {
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return date.toLocaleString(locale, { month: 'long' });
  };

  const monthVariation = (
    currentMonth: number,
    property: keyof SalesItemAttributes
  ): number => {
    const previousTarget = currentYear[currentMonth - 2].attributes[property];
    const currentTarget = currentYear[currentMonth - 1].attributes[property];

    return (
      parseInt(
        (
          ((currentTarget as number) / (previousTarget as number)) *
          100
        ).toFixed(1),
        10
      ) - 100
    );
  };

  const filteredGoals = goals.filter((el: GoalsItem) => {
    if (el.attributes.quarter === 'q' + activeQuarter) {
      return el;
    }
  });

  const getDepartments = [
    ...new Set(filteredGoals.map((item) => item.attributes.department))
  ];

  const sortedDepartments = [...getDepartments].sort();

  const sortedGoals = filteredGoals.sort(
    (a, b) => (a.attributes.order as number) - (b.attributes.order as number)
  );

  const sortedGoalsByDepartment = sortedDepartments.map((obj) => {
    const filteredItems = sortedGoals.filter((item) => {
      if (item.attributes.department === obj) {
        return item;
      }
    });
    return filteredItems;
  });

  return (
    <StyledWrapper>
      <StyledTitle>{t('battle-report.report.title')}</StyledTitle>
      <StyledTabs>
        {[...Array(quarters)].map((e, index) => (
          <StyledTab
            key={index}
            type="button"
            role="tab"
            aria-selected={activeQuarter === index + 1}
            aria-label={`Quarter ${activeQuarter}`}
            aria-controls={`quarter${activeQuarter}-tabpanel`}
            id={`quarter${activeQuarter}-tab`}
            className={`${activeQuarter === index + 1 ? 'is-active' : ''}`}
            onClick={() => {
              setTab(index + 1);
            }}
          >
            Q{index + 1}
          </StyledTab>
        ))}
        {[...Array(emptyQuarters)].map((e, index) => (
          <StyledTab key={index} disabled type="button">
            Q{quarters ? index + quarters + 1 : undefined}
          </StyledTab>
        ))}
      </StyledTabs>
      <div
        id={`quarter${activeQuarter}-tabpanel`}
        role="tabpanel"
        aria-labelledby={`quarter${activeQuarter}-tab`}
        tabIndex={0}
      >
        <StyledSectionTitle>
          <FontAwesomeIcon icon={faCashRegister} />
          <span>{t('battle-report.report.numbers-title')}</span>
        </StyledSectionTitle>
        <StyledData>
          <QuarterChart sales={filteredQuarter} />
          <StyledDataMonthList>
            {filteredQuarter.map((e: SalesItem, index: number) => (
              <StyledDataMonth key={index}>
                <StyledDataMonthTitle>
                  {getMonthName(e.attributes.month)}
                </StyledDataMonthTitle>
                <StyledTendency>
                  <StyledTendencyTitle>
                    {t('battle-report.report.sales')}
                  </StyledTendencyTitle>
                  <StyledTendencyValue>
                    <span>{e.attributes.sales.toLocaleString(locale)}€</span>
                    {e.attributes.month !== 1 && (
                      <StyledTendencyVariation
                        className={classnames({
                          positive:
                            monthVariation(e.attributes.month, 'sales') > 0,
                          negative:
                            monthVariation(e.attributes.month, 'sales') < 0,
                          neutral:
                            monthVariation(e.attributes.month, 'sales') === 0
                        })}
                      >
                        <FontAwesomeIcon
                          icon={
                            monthVariation(e.attributes.month, 'sales') > 0
                              ? faArrowCircleUp
                              : monthVariation(e.attributes.month, 'sales') < 0
                              ? faArrowCircleDown
                              : faEquals
                          }
                        />
                        {monthVariation(e.attributes.month, 'sales') !== 0 && (
                          <>{monthVariation(e.attributes.month, 'sales')}%</>
                        )}
                      </StyledTendencyVariation>
                    )}
                  </StyledTendencyValue>
                </StyledTendency>
                <StyledTendency>
                  <StyledTendencyTitle>
                    {t('battle-report.report.turnover')}
                  </StyledTendencyTitle>
                  <StyledTendencyValue>
                    <span>{e.attributes.billing.toLocaleString(locale)}€</span>
                    {e.attributes.month !== 1 && (
                      <StyledTendencyVariation
                        className={classnames({
                          positive:
                            monthVariation(e.attributes.month, 'billing') > 0,
                          negative:
                            monthVariation(e.attributes.month, 'billing') < 0,
                          neutral:
                            monthVariation(e.attributes.month, 'billing') === 0
                        })}
                      >
                        <FontAwesomeIcon
                          icon={
                            monthVariation(e.attributes.month, 'billing') > 0
                              ? faArrowCircleUp
                              : monthVariation(e.attributes.month, 'billing') <
                                0
                              ? faArrowCircleDown
                              : faEquals
                          }
                        />
                        {monthVariation(e.attributes.month, 'billing') !==
                          0 && (
                          <>{monthVariation(e.attributes.month, 'billing')}%</>
                        )}
                      </StyledTendencyVariation>
                    )}
                  </StyledTendencyValue>
                </StyledTendency>
              </StyledDataMonth>
            ))}
          </StyledDataMonthList>
        </StyledData>
        <StyledSectionTitle>
          <FontAwesomeIcon icon={faBullseyeArrow} />
          <span>{t('battle-report.report.objectives-title')}</span>
        </StyledSectionTitle>
        {sortedGoalsByDepartment.map((goalGroup, index) => (
          <div key={index}>
            <StyledGoalsTitle>
              {goalGroup[0].attributes.department}
            </StyledGoalsTitle>
            {goalGroup?.map((goal, index) => (
              <StyledGoal key={index}>
                <h6>
                  <StyledGoalIcon className="positive">
                    <FontAwesomeIcon icon={faArrowCircleUp} />
                  </StyledGoalIcon>
                  <span>{goal.attributes.goalTitle}</span>
                </h6>
                {goal.attributes.goalDescription && (
                  <MarkdownComponent
                    content={goal.attributes.goalDescription}
                  />
                )}
              </StyledGoal>
            ))}
          </div>
        ))}
      </div>
    </StyledWrapper>
  );
};

export default BattleReportQuarter;
